import { apiClient } from '../apiClient';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const getRoleCatalog = async () => {
  const response = await apiClient.get('/auth/roles/catalog', {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const listAccessUsers = async ({ page = 1, limit = 100, search = '' } = {}) => {
  const response = await apiClient.get('/auth/users', {
    params: { page, limit, search },
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const listAccessAuditLogs = async ({
  page = 1,
  limit = 50,
  search = '',
  action = '',
  entityType = '',
  performedBy = '',
  status = '',
  excludeOffice = false,
  excludeOmnichannel = false,
} = {}) => {
  const response = await apiClient.get('/auth/audit-logs', {
    params: { page, limit, search, action, entityType, performedBy, status, excludeOffice, excludeOmnichannel },
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const getAccessAuditLoginSummary = async ({
  performedBy = '',
  excludeOffice = false,
  excludeOmnichannel = false,
  dayRange = 30,
  weekRange = 12,
} = {}) => {
  const response = await apiClient.get('/auth/audit-logs/login-summary', {
    params: { performedBy, excludeOffice, excludeOmnichannel, dayRange, weekRange },
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const getServerHealthStatus = async ({ limit = 24, range = '24h' } = {}) => {
  const response = await apiClient.get('/server-health', {
    params: { limit, range },
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const createServerHealthSnapshot = async () => {
  const response = await apiClient.post(
    '/server-health/snapshot',
    {},
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const updateAccessUserRoles = async (userId, roles = []) => {
  const response = await apiClient.put(
    `/auth/users/${userId}/roles`,
    { roles },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const disableAccessUser = async (userId, reason = '') => {
  const response = await apiClient.post(
    `/auth/users/${encodeURIComponent(userId)}/disable`,
    { reason },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const enableAccessUser = async (userId) => {
  const response = await apiClient.post(
    `/auth/users/${encodeURIComponent(userId)}/enable`,
    {},
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const listAccessUserPushDevices = async (userId) => {
  const response = await apiClient.get(`/auth/users/${encodeURIComponent(userId)}/push-devices`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const sendAccessUserTestPush = async (userId, payload = {}) => {
  const response = await apiClient.post(
    `/auth/users/${encodeURIComponent(userId)}/push-devices/test`,
    payload,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const revokeAccessUserPushDevice = async (userId, deviceId, payload = {}) => {
  const response = await apiClient.post(
    `/auth/users/${encodeURIComponent(userId)}/push-devices/${encodeURIComponent(deviceId)}/revoke`,
    payload,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const createAccessInvitation = async ({ email, roles = ['viewer'], expiresInDays = 7 } = {}) => {
  const response = await apiClient.post(
    '/auth/invitations',
    { email, roles, expiresInDays },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const listAccessInvitations = async ({ page = 1, limit = 100, status = '', email = '' } = {}) => {
  const response = await apiClient.get('/auth/invitations', {
    params: { page, limit, status, email },
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const deleteAccessInvitation = async (invitationId) => {
  const response = await apiClient.delete(`/auth/invitations/${encodeURIComponent(invitationId)}`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const getInvitationDetails = async (token) => {
  const response = await apiClient.get(`/auth/invitations/${encodeURIComponent(token)}`);
  return response.data;
};

export const listRoles = async () => {
  const response = await apiClient.get('/roles', {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const createRole = async ({ name, permissions = [], menuAccess = [] } = {}) => {
  const response = await apiClient.post(
    '/roles/create',
    { name, permissions, menuAccess },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const updateRole = async ({ roleId, permissions = [], menuAccess = [] } = {}) => {
  const response = await apiClient.put(
    '/roles/update',
    { roleId, permissions, menuAccess },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const deleteRole = async (roleId) => {
  const response = await apiClient.delete('/roles/delete', {
    data: { roleId },
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const listPermissionCatalog = async ({ search = '', activeOnly = false } = {}) => {
  const response = await apiClient.get('/permissions', {
    params: { search, activeOnly },
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const createPermissionCatalog = async ({ key, label = '', description = '', isActive = true } = {}) => {
  const response = await apiClient.post(
    '/permissions',
    { key, label, description, isActive },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const updatePermissionCatalog = async (
  permissionId,
  { key, label = '', description = '', isActive = true } = {}
) => {
  const response = await apiClient.put(
    `/permissions/${permissionId}`,
    { key, label, description, isActive },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const deletePermissionCatalog = async (permissionId) => {
  const response = await apiClient.delete(`/permissions/${permissionId}`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};
